import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CosmicBackground } from "../components/CosmicBackground";
import { Bodygraph } from "../components/Bodygraph";
import { NatalChartCard } from "../components/NatalChartCard";
import { GeneKeysCard } from "../components/GeneKeysCard";
import { OnboardingModal } from "../components/OnboardingModal";
import { QuestList } from "../components/QuestList";
import { AURA_COLORS } from "../lib/aura";
import { Button } from "../components/ui/button";
import { Stars, LogOut, Sparkles } from "lucide-react";
import { Loader2 } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [charts, setCharts] = useState(null);
  const [questPayload, setQuestPayload] = useState(null);
  const [geneKeysDetails, setGeneKeysDetails] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      if (!me.data.onboarding_complete) {
        setShowOnboarding(true);
        setLoading(false);
        return;
      }
      setShowOnboarding(false);
      const [chartRes, questsRes, gkRes] = await Promise.all([
        api.get("/charts/me"),
        api.get("/quests"),
        api.get("/gene-keys/me").catch(() => ({ data: { details: [] } })),
      ]);
      setCharts(chartRes.data);
      setQuestPayload(questsRes.data);
      setGeneKeysDetails(gkRes.data?.details || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/", { replace: true });
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#05070A] text-[#F8FAFC]">
        <Loader2 className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const hd = charts?.human_design;
  const auraColor = AURA_COLORS[hd?.type] || AURA_COLORS.Generator;

  return (
    <CosmicBackground>
      <header className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="dashboard-logo">
          <Stars size={20} strokeWidth={1.5} className="text-[#D4AF37]" />
          <span className="font-serif-mystic text-xl tracking-wide">AuraQuest</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/compatibility")}
            data-testid="nav-compatibility"
            className="text-sm text-[#94A3B8] hover:text-[#D4AF37] transition-colors hidden sm:inline"
          >
            Compatibility
          </button>
          <button
            onClick={() => navigate("/astrocartography")}
            data-testid="nav-astrocartography"
            className="text-sm text-[#94A3B8] hover:text-[#D4AF37] transition-colors hidden sm:inline"
          >
            Astrocartography
          </button>
          <button
            onClick={() => navigate("/pricing")}
            data-testid="nav-pricing"
            className="text-sm text-[#D4AF37] hover:text-[#F3E5AB] transition-colors"
          >
            {user?.plan === "free" ? "Upgrade" : "Plans"}
          </button>
          {user?.preferred_name && (
            <span className="text-sm text-[#94A3B8] hidden sm:inline" data-testid="user-greeting">
              {user.preferred_name}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            data-testid="logout-btn"
            className="text-[#94A3B8] hover:text-white"
          >
            <LogOut size={14} strokeWidth={1.5} className="mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <OnboardingModal open={showOnboarding} onComplete={loadAll} />

      {!showOnboarding && hd && (
        <main className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
          {/* Hero strip */}
          <section className="mb-10" data-testid="hero-strip">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
              Your Energetic Signature
            </p>
            <h1 className="font-serif-mystic text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              You are a <span style={{ color: auraColor }}>{hd.type}</span>
            </h1>
            <p className="text-[#94A3B8] mt-3 max-w-2xl text-base">
              Strategy: <span className="text-white">{hd.strategy}</span> · Authority:{" "}
              <span className="text-white">{hd.authority}</span> · Profile:{" "}
              <span className="text-white">{hd.profile}</span>
            </p>
          </section>

          {/* Bento grid */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div
              className="rounded-xl border border-white/10 bg-[#111625]/80 backdrop-blur-xl p-6 lg:col-span-2 h-full"
              data-testid="bodygraph-card"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#94A3B8]">
                    Human Design Bodygraph
                  </p>
                  <h2 className="font-serif-mystic text-2xl mt-1">Your Aura, Glowing</h2>
                </div>
                <Sparkles size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <div className="aspect-[3/4] sm:aspect-[4/3] max-h-[520px] mx-auto">
                <Bodygraph hdType={hd.type} definedCenters={hd.defined_centers || []} />
              </div>
            </div>
            <div className="h-full">
              <NatalChartCard natal={charts?.natal} />
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <GeneKeysCard geneKeys={hd.gene_keys} details={geneKeysDetails} />
            <div
              className="rounded-xl border border-white/10 bg-[#111625]/80 backdrop-blur-xl p-6"
              data-testid="vedic-card"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-[#94A3B8]">Vedic · Sidereal</p>
              <h3 className="font-serif-mystic text-2xl mt-1 mb-3">Eastern Lens</h3>
              <p className="text-sm text-[#94A3B8]">
                Defined centers:{" "}
                <span className="text-white">
                  {(hd.defined_centers || []).join(", ") || "—"}
                </span>
              </p>
              <p className="text-sm text-[#94A3B8] mt-2">
                Definition: <span className="text-white">{hd.definition}</span>
              </p>
            </div>
          </section>

          <section className="mt-8">
            <QuestList
              quests={questPayload?.quests || []}
              xp={questPayload?.xp || 0}
              plan={questPayload?.plan || "free"}
              onRefresh={loadAll}
            />
          </section>
        </main>
      )}
    </CosmicBackground>
  );
}
